<script setup>
import { ref } from "vue";
import { enableSelfExclusion } from "@/app/api";
import { toast } from "@/app/utils/notification";
import { useI18n } from "@/app/composables/useI18n";
import { useAuth } from "@/user-account/composables/useAuth";
import { useBetStore } from "/store";
import BaseButton from "@/app/components/ui/BaseButton.vue";
import BaseSelectField from "@/app/components/ui/BaseSelectField.vue";

const { t } = useI18n();
const { logout } = useAuth();
const store = useBetStore();

const periods = ref([
  {
    label: t("common.24_hours"),
    value: "24_hours",
  },
  {
    label: `1 ${t("common.week")}`,
    value: "1_week",
  },
  {
    label: `1 ${t("common.month")}`,
    value: "1_month",
  },
  {
    label: t("common.90_days"),
    value: "90_days",
  },
]);
const selectedPeriod = ref(null);
const showConfirm = ref(false);

function getPeriodLabel() {
  const found = periods.value.find(p => p.value === selectedPeriod.value);
  return found ? found.label : "";
}

async function enableCoolOff() {
  showConfirm.value = true;
}

async function confirmCoolOff() {
  try {
    await enableSelfExclusion({
      cool_off_before: selectedPeriod.value
    });

    toast.info(t("cooloff.success_message"));
    store.handleShowBetModal(false);
    logout();
  } catch (error) {
    if (error.response?.status === 400 && error.response?.data?.error_code === 'active_cool_off') {
      toast.error(t("cooloff.active_cool_off_error") || "You already have an active cool off period");
    } else {
      toast.error(t("cooloff.error_message"));
    }
  }
  showConfirm.value = false;
}
</script>

<template>
  <div class="container">
    <div class="form">
      <div class="form-fields">
        <div class="title">
          {{ t("cooloff.title") }}
        </div>
        <div class="subtitle">
          {{ t("cooloff.subtitle") }}
        </div>
        <BaseSelectField
          :placeholder="t('cooloff.placeholder')"
          :options="periods"
          @update:model-value="selectedPeriod = $event"
        />
      </div>
      <BaseButton class="submit alight wide high" type="button" @click="enableCoolOff" :disabled="!selectedPeriod">
        {{ t("account.save") }}
      </BaseButton>
    </div>
    <!-- Inline modal for confirmation -->
    <Transition name="modal">
      <div v-if="showConfirm" class="modal-mask">
        <div class="modal-container">
          <div class="modal-detail">
            <div class="close-btn" @click="showConfirm = false">
              <div>{{ t("general.close") }}</div>
              <img src="/assets/images/close-modal-icon.svg" alt="" />
            </div>
            <div class="modal-body">
              <div class="modal-title">{{ t('cooloff.confirm_title') }}</div>
              <div>
                {{ t('cooloff.confirm_message', { period: getPeriodLabel() }) }}
              </div>
              <BaseButton class="submit alight wide high margin-top" type="button" @click="confirmCoolOff">
                {{ t("account.save") }}
              </BaseButton>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
.title {
  font-size: 16px;
  font-weight: 600;
  color: var(--be-primary-text-color);
}

.subtitle {
  font-size: 14px;
  font-weight: 400;
  color: var(--be-primary-text-color);
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
}

.margin-top {
  margin-top: 15px;
}

.label {
  font-size: 14px;
  margin-bottom: 7px;
}

@media screen and (max-width: 500px) {
  .container {
    height: 100%;
  }
}

.form {
  border-radius: 8px;
  background: var(--be-big-card-bg);
  padding: 16px;
  margin-bottom: 1rem;
}

.btn-g {
  color: var(--be-primary-btn-label);
  border: none;
  height: 42px;
  width: 100%;
  border-radius: 4px;
  background: var(--Linear, linear-gradient(90deg, #3886ec 0%, #544fe5 100%));
  margin: 20px 0px;
  font-size: 14px;
}

/* Modal styles (copied from Login/Register modals) */
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(5px);
  display: flex;
  transition: opacity 0.3s ease;
}

.modal-container {
  width: 491px;
  margin: auto;
  padding: 39px 20px 20px 20px;
  height: 100%;
  overflow: auto;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
}

.modal-detail {
  background: var(--be-bg);
  padding: 20px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  margin-top: 13px;
  border-radius: 20px;
  margin: auto;
  position: relative;
  width: 100%;
}

.close-btn {
  color: white;
  position: absolute;
  top: -32px;
  right: 3px;
  display: flex;
  gap: 0 5px;
  cursor: pointer;
}

.modal-body {
  border-radius: 8px;
  background: var(--be-big-card-bg);
  padding: 16px;
}

.modal-title {
  font-size: 1.2em;
  font-weight: 700;
  margin-bottom: 1em;
}
</style>
